import { yupResolver } from '@hookform/resolvers/yup'
import { KIND } from 'baseui/button'
import { FormControl } from 'baseui/form-control'
import { Input } from 'baseui/input'
import {
  Modal,
  ModalBody,
  ModalButton,
  ModalFooter,
  ModalHeader,
} from 'baseui/modal'
import { TimezonePicker } from 'baseui/timezonepicker'
import { Location } from 'client'
import React, { useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { createCallbackRef } from 'use-callback-ref'
import * as yup from 'yup'

const AddLocationDialog: React.FC<{
  isOpen: boolean
  onClose: (location: Location) => void
  onCancel: () => void
}> = React.memo(({ isOpen, onClose, onCancel }) => {
  const [t] = useTranslation()
  const locationSchema: yup.ObjectSchema<Location> = useMemo(() => {
    return yup.object({
      id: yup.number(),
      name: yup
        .string()
        .required(t('validation.required'))
        .label(t('settings.location.name')),
      latitude: yup.number(),
      longitude: yup.number(),
      timezone: yup
        .string()
        .required(t('validation.required'))
        .label(t('settings.location.timezone')),
      tenantId: yup.string(),
    })
  }, [t])

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<Location>({
    resolver: yupResolver(locationSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      name: '',
      timezone: 'Europe/Berlin',
    },
  })

  return (
    <Modal isOpen={isOpen} closeable={false}>
      <form
        onSubmit={handleSubmit((location: Location) => {
          onClose(location)
          reset()
        })}
      >
        <ModalHeader>{t('settings.location.add_location')}</ModalHeader>
        <ModalBody>
          <FormControl
            label={t('settings.location.name')}
            error={errors?.name?.message}
          >
            <Controller
              name={'name'}
              control={control}
              render={({ field: { onChange, onBlur, value, ref } }) => (
                <Input
                  onChange={onChange}
                  onBlur={onBlur}
                  placeholder={t('settings.location.name')}
                  value={value}
                  error={!!errors?.name}
                  inputRef={createCallbackRef(ref)}
                />
              )}
            />
          </FormControl>
          <FormControl
            label={t('settings.location.timezone')}
            error={errors?.timezone?.message}
          >
            <Controller
              name={'timezone'}
              control={control}
              render={({ field: { onChange, onBlur, value, ref } }) => (
                <TimezonePicker
                  onChange={(value) => {
                    if (value) {
                      onChange(value?.id)
                    }
                  }}
                  value={value}
                  error={!!errors?.timezone}
                  overrides={{
                    Select: {
                      props: {
                        onBlur,
                        controlRef: createCallbackRef(ref),
                      },
                    },
                  }}
                />
              )}
            />
          </FormControl>
        </ModalBody>
        <ModalFooter>
          <ModalButton
            kind={KIND.tertiary}
            onClick={() => {
              onCancel()
              reset()
            }}
            type={'button'}
          >
            {t('cancel')}
          </ModalButton>
          <ModalButton type={'submit'}>{t('ok')}</ModalButton>
        </ModalFooter>
      </form>
    </Modal>
  )
})

export { AddLocationDialog }
